/**
 * Course Details Service for TeeSync
 *
 * Fetches golf club details (phone, website, address, etc.) from the golf_clubs table.
 * Uses in-memory caching to avoid repeated database queries.
 */

import { supabase } from '../lib/supabase.js';

// In-memory cache for course details
const courseCache = new Map();

// Fields to select from golf_clubs table for display
const COURSE_DETAILS_FIELDS = `
  name,
  course_name,
  slug,
  phone,
  website,
  booking_url,
  address,
  short_formatted_address,
  city,
  state,
  postcode,
  country,
  country_code,
  google_maps_uri,
  rating,
  user_rating_count,
  regular_opening_hours,
  latitude,
  longitude
`;

/**
 * Get course details by exact or fuzzy name match
 *
 * @param {string} courseName - The course name to look up
 * @returns {Object|null} Course details object or null if not found
 */
export async function getCourseDetailsByName(courseName) {
  if (!courseName || typeof courseName !== 'string') {
    return null;
  }

  const cacheKey = courseName.toLowerCase().trim();

  // Check cache first
  if (courseCache.has(cacheKey)) {
    return courseCache.get(cacheKey);
  }

  try {
    // Exact match first
    let { data, error } = await supabase
      .from('golf_clubs')
      .select(COURSE_DETAILS_FIELDS)
      .eq('name', courseName)
      .single();

    // If no exact match, try fuzzy search (pattern from geocodingService.js)
    if (!data && error?.code === 'PGRST116') {
      const { data: fuzzyData, error: fuzzyError } = await supabase
        .from('golf_clubs')
        .select(COURSE_DETAILS_FIELDS)
        .ilike('name', `%${courseName}%`)
        .limit(1);

      if (fuzzyError) {
        console.warn('Fuzzy search error:', fuzzyError);
      }

      if (fuzzyData?.length > 0) {
        data = fuzzyData[0];
      }
    }

    // Cache and return result (including null for not found)
    const result = data || null;
    courseCache.set(cacheKey, result);
    return result;

  } catch (error) {
    console.error('Error fetching course details:', error);
    // Cache null to prevent repeated failed queries
    courseCache.set(cacheKey, null);
    return null;
  }
}

/**
 * Get course details for multiple courses in a batch
 * Optimised to minimise database queries using cache and batch fetch
 *
 * @param {string[]} courseNames - Array of course names to look up
 * @returns {Map<string, Object>} Map of course name to details object
 */
export async function getMultipleCourseDetails(courseNames) {
  if (!courseNames || courseNames.length === 0) {
    return new Map();
  }

  // Filter out null/undefined and deduplicate
  const uniqueNames = [...new Set(courseNames.filter(Boolean))];
  const results = new Map();

  // Check cache for all courses, collect cache misses
  const cacheMisses = [];
  for (const name of uniqueNames) {
    const cacheKey = name.toLowerCase().trim();
    if (courseCache.has(cacheKey)) {
      results.set(name, courseCache.get(cacheKey));
    } else {
      cacheMisses.push(name);
    }
  }

  // If all were cached, return early
  if (cacheMisses.length === 0) {
    return results;
  }

  // Batch fetch cache misses from database
  try {
    const { data, error } = await supabase
      .from('golf_clubs')
      .select(COURSE_DETAILS_FIELDS)
      .in('name', cacheMisses);

    if (error) {
      console.warn('Batch fetch error:', error);
    }

    // Create a map of fetched results by name
    const fetchedMap = new Map(
      (data || []).map(course => [course.name, course])
    );

    // Update cache and results for each cache miss
    for (const name of cacheMisses) {
      const course = fetchedMap.get(name) || null;
      const cacheKey = name.toLowerCase().trim();
      courseCache.set(cacheKey, course);
      results.set(name, course);
    }

  } catch (error) {
    console.error('Error batch fetching course details:', error);
    // Set null for all cache misses to prevent repeated queries
    for (const name of cacheMisses) {
      const cacheKey = name.toLowerCase().trim();
      courseCache.set(cacheKey, null);
      results.set(name, null);
    }
  }

  return results;
}

/**
 * Clear the course details cache
 * Useful for forcing fresh data after updates
 */
export function clearCourseCache() {
  courseCache.clear();
}

/**
 * Get the current cache size (for debugging)
 * @returns {number} Number of cached entries
 */
export function getCacheSizeDebug() {
  return courseCache.size;
}
